// 시임1+시임2 — LLM 호출 단일 통과 지점(loggedCompletion) + 백엔드 호출 계측 + 턴 단위 콜렉터 //
// P2a (2026-06-11): 모든 chat.completions.create는 본 모듈의 loggedCompletion을 통과 — 토큰 전수 포착(C2 해소).
// P2b (2026-06-11): 백엔드 호출(callApiInternal 시임)은 recordBackendCall로 적재 — URL/상태/지연/원시응답(H4 해소).
// 소비처: useAiChat(라이브 5곳: title/sql_gen/sql_retry/tool_loop/sub) + smsHandlers(2곳: sms_bulk/sms_individual)
//        + AgentSession/nodeAgent callApiInternal(시임2 dual-sync). 텔레메트리는 에이전트 동작에 절대 영향 금지 — 기록 실패 전부 무시.
// 설계: docs/analysis/2026-06/2026-06-W2/2026-06-11-ai-telemetry-design-adversarial-verification-analysis.md §5~7
//  - V-3: tool_loop rawRequest prefix-dedup(직전 호출 drop, messageCount 잔존) — O(k²)→O(k), gzip은 P2c 페이로드 빌드에서
//  - V-5: promptVersions는 턴 meta로 보관 (promptStore.getPromptVersions)
//  - E(시임2 스코프): 전역 FetchModule 비침투 — AI 세션의 callApiInternal 내부에서만 계측

// rawRequest 문자열 상한 — 초과 시 절단 + 플래그 (메모리 보호) //
const MAX_RAW_REQUEST_CHARS = 500000;

// 호출 파라미터 중 적재 대상 (ai_response.model_params) //
const MODEL_PARAM_KEYS = ['temperature', 'top_p', 'max_tokens', 'max_completion_tokens', 'tool_choice', 'frequency_penalty', 'presence_penalty', 'reasoning_effort'];

// usage 추출 — 부분집합 가산 금지 규약(§8): reasoning ⊂ output, cached ⊂ input //
function extractUsage(response) {
  const usage = response?.usage || {};

  return {
    inputTokens: usage.prompt_tokens ?? null,
    outputTokens: usage.completion_tokens ?? null,
    reasoningTokens: usage.completion_tokens_details?.reasoning_tokens ?? null,
    cachedTokens: usage.prompt_tokens_details?.cached_tokens ?? null,
    totalTokens: usage.total_tokens ?? null,
  };
}

// 호출 파라미터 추출 (JSON string) — 실패 시 null //
function extractModelParams(requestArgs) {
  try {
    const params = {};

    for (const key of MODEL_PARAM_KEYS) {
      if (requestArgs && requestArgs[key] !== undefined && key !== 'tool_choice') params[key] = requestArgs[key];
    }

    if (requestArgs?.tool_choice !== undefined) params.tool_choice = typeof requestArgs.tool_choice === 'string' ? requestArgs.tool_choice : 'object';

    return Object.keys(params).length > 0 ? JSON.stringify(params) : null;
  }
  catch (_e) { return null; }
}

// 임의 값 JSON 직렬화 + 상한 절단 (시임2 rawResponse/params 공용) — 실패 시 null //
export function safeJsonCapped(value, maxChars) {
  try {
    if (value === undefined || value === null) return { json: null, truncated: false };

    const raw = JSON.stringify(value);

    if (typeof raw !== 'string') return { json: null, truncated: false };
    if (raw.length > maxChars) return { json: raw.slice(0, maxChars), truncated: true };

    return { json: raw, truncated: false };
  }
  catch (_e) { return { json: null, truncated: false }; }
}

// raw_request 직렬화 (messages + tools 정의) — 상한 절단 + 실패 시 null //
function serializeRawRequest(requestArgs) {
  try {
    const raw = JSON.stringify({ model: requestArgs?.model, messages: requestArgs?.messages, tools: requestArgs?.tools });

    if (raw.length > MAX_RAW_REQUEST_CHARS) return { rawRequest: raw.slice(0, MAX_RAW_REQUEST_CHARS), rawRequestTruncated: true };

    return { rawRequest: raw, rawRequestTruncated: false };
  }
  catch (_e) { return { rawRequest: null, rawRequestTruncated: false }; }
}

// 턴 단위 LLM 호출 콜렉터 — reset은 다음 턴 시작에서만 (턴 종료 후 SMS 비동기 호출도 현재 턴에 누적, V-2) //
export function createLlmCollector() {
  let calls = [];
  let backendCalls = [];
  let meta = {};
  let iteration = 0;

  return {
    // 턴 시작 초기화 (turnMeta: conversationId/sessionId/promptVersions 등) //
    reset(turnMeta = {}) {
      calls = [];
      backendCalls = [];
      meta = turnMeta || {};
      iteration = 0;
    },

    // 호출 1건 기록 — 텔레메트리 실패는 동작에 영향 금지 //
    record(entry) {
      try {
        iteration += 1;
        const record = { iteration, ...entry };

        // [V-3 prefix-dedup] tool_loop messages는 직전 호출의 superset — 직전 tool_loop rawRequest drop(messageCount 잔존)로 O(1) 메모리
        if (record.callType === 'tool_loop') {
          for (let i = calls.length - 1; i >= 0; i--) {
            if (calls[i].callType === 'tool_loop') {
              calls[i].rawRequest = null;
              calls[i].rawRequestDeduped = true;
              break;
            }
          }
        }

        calls.push(record);

        return record;
      }
      catch (_e) { return null; }
    },

    // respId 키로 산출물 후부착 (sql_gen/sql_retry 생성 SQL) — 동시 호출에도 race-free //
    attachGeneratedContent(respId, content) {
      try {
        if (!respId) return;

        const target = calls.find(c => c.respId === respId);

        if (target) target.generatedContent = content;
      }
      catch (_e) { /* 무시 */ }
    },

    // [시임2] 백엔드 호출 1건 기록 (callApiInternal에서 호출) — 텔레메트리 실패는 동작에 영향 금지 //
    recordBackendCall(entry) {
      try {
        const record = { index: backendCalls.length + 1, ...entry };

        backendCalls.push(record);

        return record;
      }
      catch (_e) { return null; }
    },

    getCalls() { return calls; },
    getBackendCalls() { return backendCalls; },
    getMeta() { return meta; },
  };
}

// LLM 호출 단일 통과 지점 생성 — fetcher 주입형 (라이브/하니스 aiFetch, SMS는 null=직접 호출로 기존 동작 보존) //
export function createLoggedCompletion({ getClient, collector, fetcher = null }) {
  return async function loggedCompletion(callType, requestArgs) {
    const startTime = Date.now();
    const exec = () => getClient().chat.completions.create(requestArgs);

    try {
      const response = await (fetcher ? fetcher(exec) : exec());

      try {
        collector.record({
          respId: response?.id || null,
          callType,
          modelUsed: response?.model || requestArgs?.model || null,
          maxOutputTokens: requestArgs?.max_completion_tokens ?? requestArgs?.max_tokens ?? null,
          ...extractUsage(response),
          createdAt: response?.created ?? null,
          elapsedMs: Date.now() - startTime,
          error: false,
          errorDetails: null,
          messageCount: Array.isArray(requestArgs?.messages) ? requestArgs.messages.length : 0,
          ...serializeRawRequest(requestArgs),
          rawResponse: safeJsonCapped(response, 100000).json,
          modelParams: extractModelParams(requestArgs),
          generatedContent: null,
        });
      }
      catch (_e) { /* 텔레메트리 기록 실패 무시 — 응답은 정상 반환 */ }

      return response;
    }
    catch (error) {
      try {
        collector.record({
          respId: null,
          callType,
          modelUsed: requestArgs?.model || null,
          maxOutputTokens: requestArgs?.max_completion_tokens ?? requestArgs?.max_tokens ?? null,
          inputTokens: null, outputTokens: null, reasoningTokens: null, cachedTokens: null, totalTokens: null,
          createdAt: null,
          elapsedMs: Date.now() - startTime,
          error: true,
          errorDetails: String(error?.message || error),
          messageCount: Array.isArray(requestArgs?.messages) ? requestArgs.messages.length : 0,
          ...serializeRawRequest(requestArgs),
          modelParams: extractModelParams(requestArgs),
          generatedContent: null,
        });
      }
      catch (_e) { /* 무시 */ }

      throw error;  // 동작 보존 — 호출처의 에러 처리 경로 그대로
    }
  };
}
